/**
 * Hook for adding participants to a conversation.
 */

import type { ConversationParticipants } from '@saaga/shared/interfaces/conversation.interface'
import { useAddConversationParticipantMutation } from '@saaga/shared/store/api.store'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useMemo } from 'react'
import { toast } from 'sonner'

import type { SelectableMember } from '@/components/chat/add-participant-member-list.molecule'
import { useDialogState } from '@/hooks/use-dialog-state.hook'
import type { UserLookupMap } from '@/hooks/use-org-members-lookup.hook'

import { buildAvailableMemberList } from './available-members.helper'

interface UseAddParticipantParams {
  conversationId: string | null
  participants: ConversationParticipants | undefined
  userLookup: UserLookupMap
  currentUserId: string | undefined
}

export interface UseAddParticipantReturn {
  isDialogOpen: boolean
  availableMemberList: SelectableMember[]
  isMembersLoading: boolean
  isAddingParticipant: boolean
  openDialog: () => void
  closeDialog: () => void
  setDialogOpen: (isOpen: boolean) => void
  handleAddParticipant: (userId: string) => Promise<void>
}

export const useAddParticipant = ({
  conversationId,
  participants,
  userLookup,
  currentUserId,
}: UseAddParticipantParams): UseAddParticipantReturn => {
  const dialog = useDialogState()
  const [addParticipant, { isLoading: isAddingParticipant }] =
    useAddConversationParticipantMutation()

  const availableMemberList = useMemo<SelectableMember[]>(
    () => buildAvailableMemberList({ userLookup, participants, currentUserId }),
    [userLookup, participants, currentUserId]
  )

  const handleAddParticipant = useCallback(
    async (userId: string) => {
      if (!conversationId) {
        toast.error('Cannot add participant: No active conversation')
        return
      }

      try {
        await addParticipant({ conversationId, payload: { user_id: userId } }).unwrap()
        const addedUser = userLookup.get(userId)
        trackEvent('participant_added', {})
        toast.success(`${addedUser?.name ?? 'User'} has been added to the conversation`)
        dialog.close()
      } catch (error) {
        logger.error('Failed to add participant:', error)
        toast.error('Failed to add participant. Please try again.')
      }
    },
    [conversationId, addParticipant, userLookup, dialog]
  )

  return {
    isDialogOpen: dialog.isOpen,
    availableMemberList,
    isMembersLoading: userLookup.size === 0,
    isAddingParticipant,
    openDialog: dialog.open,
    closeDialog: dialog.close,
    setDialogOpen: dialog.setIsOpen,
    handleAddParticipant,
  }
}
